// assets/xero-utils.js
// Shared JS helpers for the xs- section system.
// NOTE: not yet loaded on the storefront — the <script> tag in snippets/script-tags.liquid
// is commented out until the first xero_* section needs these helpers (DEV-3010).
// Exposes four globals: xsReducedMotion(), xsReveal(elements, opts),
// xsWordReveal(el, opts), xsVideoToggle(video, toggle).

(function() {
  'use strict';

  // Respect reduced motion globally
  window.xsReducedMotion = function() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // IntersectionObserver-based reveal
  window.xsReveal = function(elements, options) {
    if (window.xsReducedMotion()) {
      elements.forEach(el => el.classList.add('xs-in'));
      return;
    }
    const opts = Object.assign({
      threshold: 0.2,
      rootMargin: '0px 0px -10% 0px',
      stagger: 80
    }, options || {});

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          const delay = i * opts.stagger;
          setTimeout(() => entry.target.classList.add('xs-in'), delay);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: opts.threshold, rootMargin: opts.rootMargin });

    elements.forEach(el => io.observe(el));
  };

  // Word-by-word headline reveal
  window.xsWordReveal = function(el, options) {
    if (!el || !(el.textContent || '').trim()) return;
    if (window.xsReducedMotion()) return;
    const opts = Object.assign({ stagger: 80, duration: 600 }, options || {});
    const text = el.textContent;
    el.textContent = '';
    el.setAttribute('aria-label', text);
    text.split(/\s+/).forEach((word, i) => {
      const span = document.createElement('span');
      span.textContent = word + ' ';
      span.style.display = 'inline-block';
      // inline-block collapses the trailing space above, so the words would
      // otherwise render with no gap. Right-only margin restores the breathing
      // room while keeping the first word flush with surrounding copy.
      span.style.marginRight = '0.25em';
      span.style.opacity = '0';
      span.style.transform = 'translate3d(0, 12px, 0)';
      span.style.transition = `opacity ${opts.duration}ms var(--xs-ease-out), transform ${opts.duration}ms var(--xs-ease-out)`;
      span.style.transitionDelay = (i * opts.stagger) + 'ms';
      el.appendChild(span);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          span.style.opacity = '1';
          span.style.transform = 'translate3d(0, 0, 0)';
        });
      });
    });
  };

  // Play/pause control for autoplaying background videos (WCAG 2.2.2).
  // Pair with snippets/xero-video-toggle.liquid (.xs-video-toggle styles live in
  // xero-base.css). The label/icon mirrors the real playback state via the
  // video's own play/pause events, so it stays honest no matter who started or
  // stopped it. Sets video.__xsUserPaused so section scripts that auto-play on
  // scroll (e.g. Double Hero) can keep a manual pause sticky.
  window.xsVideoToggle = function(video, toggle) {
    if (!video || !toggle || toggle.__xsVideoToggleBound) return;
    toggle.__xsVideoToggleBound = true;

    const playLabel = toggle.getAttribute('data-xs-play-label') || 'Play video';
    const pauseLabel = toggle.getAttribute('data-xs-pause-label') || 'Pause video';

    function sync() {
      const playing = !video.paused;
      toggle.dataset.state = playing ? 'playing' : 'paused';
      toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    }

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);

    toggle.addEventListener('click', () => {
      if (video.paused) {
        // Explicit opt-in plays even under reduced motion.
        video.__xsUserPaused = false;
        video.play().catch(() => {});
      } else {
        // Sticky: keep it paused even if a script tries to resume it.
        video.__xsUserPaused = true;
        video.pause();
      }
    });

    sync();
  };

  // Theme editor support — re-init on section reload.
  //
  // CONTRACT (read before building a xero_* section): these handlers only *re-broadcast*
  // Shopify's editor events as `xs:section:init` / `xs:section:destroy`. They deliberately do
  // NOT call xsReveal() themselves, because this file doesn't know which elements a given
  // section animates or with what options.
  //
  // Therefore each xero_* section is responsible for its own re-init in the theme editor:
  //   document.addEventListener('xs:section:init', (e) => {
  //     const root = document.getElementById('shopify-section-' + e.detail.sectionId);
  //     if (root) xsReveal(root.querySelectorAll('.xs-reveal'));
  //   });
  // Without that listener, a section's reveals will fire on first page load but will NOT
  // replay when the section is added/edited in the editor (elements stay at opacity:0).
  document.addEventListener('shopify:section:load', (e) => {
    document.dispatchEvent(new CustomEvent('xs:section:init', { detail: { sectionId: e.detail.sectionId } }));
  });
  document.addEventListener('shopify:section:unload', (e) => {
    document.dispatchEvent(new CustomEvent('xs:section:destroy', { detail: { sectionId: e.detail.sectionId } }));
  });
})();
